import { useState, useEffect } from 'react';
import { layoutViewportHeight } from '@/lib/viewport';

const KEYBOARD_HEIGHT_THRESHOLD_PX = 130;

/**
 * Heuristic for mobile software keyboard using the Visual Viewport API.
 * Tracks the largest layout height seen so this works when either
 * `innerHeight` or `visualViewport.height` shifts (iOS vs Android browsers).
 *
 * When `enabled` is false, always returns false.
 */
export function useVisualKeyboardOpen(enabled: boolean): boolean {
    const [keyboardOpen, setKeyboardOpen] = useState(false);

    useEffect(() => {
        if (!enabled || typeof window === 'undefined') {
            setKeyboardOpen(false);
            return;
        }

        const vv = window.visualViewport;
        if (!vv) {
            setKeyboardOpen(false);
            return;
        }

        let maxLayoutHeight = window.innerHeight;
        // Zoom-corrected throughout: a pinch shrinks vv.height exactly like a keyboard.
        let lastVvHeight = layoutViewportHeight(vv);

        const update = () => {
            maxLayoutHeight = Math.max(maxLayoutHeight, window.innerHeight);
            const obscured = Math.max(0, maxLayoutHeight - layoutViewportHeight(vv));
            setKeyboardOpen(obscured > KEYBOARD_HEIGHT_THRESHOLD_PX);
            lastVvHeight = layoutViewportHeight(vv);
        };

        const resetMaxLayoutHeight = () => {
            maxLayoutHeight = window.innerHeight;
            requestAnimationFrame(update);
        };

        const onWindowResize = () => {
            const growth = layoutViewportHeight(vv) - lastVvHeight;
            if (growth > 80) {
                resetMaxLayoutHeight();
                return;
            }
            update();
        };

        vv.addEventListener('resize', update);
        window.addEventListener('resize', onWindowResize);
        window.addEventListener('orientationchange', resetMaxLayoutHeight);
        const screenOrientation = window.screen.orientation;
        screenOrientation?.addEventListener('change', resetMaxLayoutHeight);

        const scheduleUpdate = () => requestAnimationFrame(update);
        document.addEventListener('focusin', update);
        document.addEventListener('focusout', scheduleUpdate);

        update();

        return () => {
            vv.removeEventListener('resize', update);
            window.removeEventListener('resize', onWindowResize);
            window.removeEventListener('orientationchange', resetMaxLayoutHeight);
            screenOrientation?.removeEventListener('change', resetMaxLayoutHeight);
            document.removeEventListener('focusin', update);
            document.removeEventListener('focusout', scheduleUpdate);
        };
    }, [enabled]);

    return keyboardOpen;
}
